/* OwnerAdminTelemetry — funnel visualizations for the 5 conversion surfaces.
 *
 * Reads aggregated counts from /api/telemetry/funnel and renders:
 *   - Time-window selector (7 / 30 / 90 days)
 *   - Surface cards for each conversion surface with key drop-off rates
 *   - Bar chart of top events
 *
 * Owner-admin only — route guarded by OwnerAdminLayout.
 */
import { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import {
  BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip, CartesianGrid,
} from 'recharts';
import {
  Activity, Sparkles, RefreshCw, AlertTriangle, ArrowDown, ArrowUp, Star,
  Loader2, ClipboardCheck, FolderTree, BellRing,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const SURFACES = [
  {
    key: 'wizard',
    label: 'Setup Wizard',
    icon: ClipboardCheck,
    prefix: 'wizard.',
    description: 'First-time 3-step onboarding overlay',
    funnel: [
      { event: 'wizard.auto_opened', label: 'Auto-opened' },
      { event: 'wizard.opened',      label: 'Manually opened' },
      { event: 'wizard.step_completed', label: 'Step completed' },
      { event: 'wizard.finished',    label: 'Finished' },
      { event: 'wizard.dismissed',   label: 'Dismissed' },
    ],
  },
  {
    key: 'finish_banner',
    label: 'Finish Setup Banner',
    icon: BellRing,
    prefix: 'finish_banner.',
    description: 'Re-engagement banner for stalled workspaces',
    funnel: [
      { event: 'finish_banner.shown',             label: 'Shown' },
      { event: 'finish_banner.continue_clicked',  label: 'Clicked Continue' },
      { event: 'finish_banner.dismissed',         label: 'Dismissed' },
    ],
  },
  {
    key: 'favorite',
    label: 'Favorites (Quick Access)',
    icon: Star,
    prefix: 'favorite.',
    description: 'Sidebar pinning + auto-suggest toast',
    funnel: [
      { event: 'favorite.pinned',             label: 'Pinned (manual)' },
      { event: 'favorite.unpinned',           label: 'Unpinned' },
      { event: 'favorite.suggested_shown',    label: 'Toast shown' },
      { event: 'favorite.suggested_accepted', label: 'Toast → pinned' },
    ],
  },
  {
    key: 'template_card',
    label: 'Suggested Templates',
    icon: FolderTree,
    prefix: 'template_card.',
    description: 'Contextual template nudge on Documents page',
    funnel: [
      { event: 'template_card.shown',     label: 'Shown' },
      { event: 'template_card.applied',   label: 'Applied' },
      { event: 'template_card.dismissed', label: 'Dismissed' },
    ],
  },
];


function pct(numerator, denominator) {
  if (!denominator) return null;
  return Math.round((numerator / denominator) * 100);
}


function StatCell({ label, value }) {
  return (
    <div className="text-center">
      <p className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground mb-0.5">{label}</p>
      <p className="text-lg font-bold tabular-nums">{value}</p>
    </div>
  );
}


function SurfaceCard({ surface, events, loading }) {
  const Icon = surface.icon;
  // Build a map of event -> {count, unique_users, unique_workspaces}
  const byEvent = Object.fromEntries(events.map(e => [e.event, e]));

  // Top-of-funnel = first event in the surface's funnel array that has data
  const topEvent = surface.funnel
    .map(f => ({ ...f, data: byEvent[f.event] }))
    .find(f => f.data?.count > 0);

  // Conversion rates surface-specific
  let kpi = null;
  if (surface.key === 'wizard') {
    const opened = (byEvent['wizard.auto_opened']?.count || 0) + (byEvent['wizard.opened']?.count || 0);
    const finished = byEvent['wizard.finished']?.count || 0;
    const dismissed = byEvent['wizard.dismissed']?.count || 0;
    kpi = {
      primary: { label: 'Completion rate', value: pct(finished, opened), suffix: '%', tone: 'emerald' },
      secondary: { label: 'Drop-off rate', value: pct(dismissed, opened), suffix: '%', tone: 'amber' },
    };
  } else if (surface.key === 'finish_banner') {
    const shown = byEvent['finish_banner.shown']?.count || 0;
    const clicked = byEvent['finish_banner.continue_clicked']?.count || 0;
    const dismissed = byEvent['finish_banner.dismissed']?.count || 0;
    kpi = {
      primary: { label: 'Click-through', value: pct(clicked, shown), suffix: '%', tone: 'emerald' },
      secondary: { label: 'Dismiss rate', value: pct(dismissed, shown), suffix: '%', tone: 'amber' },
    };
  } else if (surface.key === 'favorite') {
    const shown = byEvent['favorite.suggested_shown']?.count || 0;
    const accepted = byEvent['favorite.suggested_accepted']?.count || 0;
    const pinned = byEvent['favorite.pinned']?.count || 0;
    kpi = {
      primary: { label: 'Auto-suggest accept', value: pct(accepted, shown), suffix: '%', tone: 'emerald' },
      secondary: { label: 'Manual pins', value: pinned, tone: 'blue' },
    };
  } else if (surface.key === 'template_card') {
    const shown = byEvent['template_card.shown']?.count || 0;
    const applied = byEvent['template_card.applied']?.count || 0;
    const dismissed = byEvent['template_card.dismissed']?.count || 0;
    kpi = {
      primary: { label: 'Apply rate', value: pct(applied, shown), suffix: '%', tone: 'emerald' },
      secondary: { label: 'Dismiss rate', value: pct(dismissed, shown), suffix: '%', tone: 'amber' },
    };
  }

  return (
    <Card data-testid={`telemetry-card-${surface.key}`}>
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-2">
          <div className="flex items-start gap-2.5">
            <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-emerald-500/15 to-violet-500/15 border border-emerald-500/20 flex items-center justify-center flex-shrink-0">
              <Icon className="w-4 h-4 text-emerald-500" />
            </div>
            <div>
              <CardTitle className="text-sm">{surface.label}</CardTitle>
              <p className="text-xs text-muted-foreground mt-0.5">{surface.description}</p>
            </div>
          </div>
        </div>
      </CardHeader>
      <CardContent className="pt-0 space-y-3">
        {loading ? (
          <div className="text-center py-6 text-sm text-muted-foreground">
            <Loader2 className="w-4 h-4 animate-spin mx-auto mb-1" /> Loading…
          </div>
        ) : !topEvent ? (
          <p className="text-xs text-muted-foreground text-center py-4">No events recorded yet.</p>
        ) : (
          <>
            {/* KPIs */}
            {kpi && (
              <div className="grid grid-cols-2 gap-2 p-2.5 rounded-lg bg-muted/40">
                <div>
                  <p className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground">{kpi.primary.label}</p>
                  <p className={`text-xl font-bold tabular-nums ${kpi.primary.tone === 'emerald' ? 'text-emerald-500' : 'text-blue-500'}`}>
                    {kpi.primary.value === null ? '—' : `${kpi.primary.value}${kpi.primary.suffix || ''}`}
                  </p>
                </div>
                <div>
                  <p className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground">{kpi.secondary.label}</p>
                  <p className={`text-xl font-bold tabular-nums ${kpi.secondary.tone === 'amber' ? 'text-amber-500' : 'text-blue-500'}`}>
                    {kpi.secondary.value === null ? '—' : `${kpi.secondary.value}${kpi.secondary.suffix || ''}`}
                  </p>
                </div>
              </div>
            )}

            {/* Funnel list */}
            <div className="space-y-1">
              {surface.funnel.map((f) => {
                const data = byEvent[f.event];
                const count = data?.count || 0;
                const users = data?.unique_users || 0;
                return (
                  <div key={f.event} className="flex items-center justify-between text-xs py-1 border-b border-border/40 last:border-0">
                    <span className="text-muted-foreground truncate flex-1">{f.label}</span>
                    <span className="font-mono tabular-nums font-medium text-foreground">{count}</span>
                    <span className="font-mono tabular-nums text-muted-foreground/80 ml-3 w-12 text-right">{users} users</span>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}


export default function OwnerAdminTelemetry() {
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await fetch(`${API}/api/telemetry/funnel?days=${days}`, { credentials: 'include' });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      setData(await r.json());
    } catch (e) {
      setError(e.message);
    }
    setLoading(false);
  };

  useEffect(() => { fetchData(); /* eslint-disable-next-line */ }, [days]);

  const events = data?.events || [];
  // Top 10 events for the chart
  const top = [...events].sort((a, b) => b.count - a.count).slice(0, 10);

  return (
    <div className="space-y-5" data-testid="owner-admin-telemetry">
      {/* Header */}
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <p className="text-[10px] font-mono uppercase tracking-[0.22em] text-muted-foreground">Conversion Analytics</p>
          <h1 className="text-2xl font-bold tracking-tight mt-1 flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-emerald-500" />
            Telemetry
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            How users interact with each conversion surface across the last {days} days.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {[7, 30, 90, 365].map((d) => (
            <Button
              key={d}
              size="sm"
              variant={days === d ? 'default' : 'outline'}
              onClick={() => setDays(d)}
              data-testid={`telemetry-days-${d}`}
            >
              {d}d
            </Button>
          ))}
          <Button size="sm" variant="outline" onClick={fetchData} disabled={loading} data-testid="telemetry-refresh">
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </div>

      {error && (
        <Card className="border-red-500/40 bg-red-500/5">
          <CardContent className="p-4 flex items-center gap-2 text-sm text-red-500">
            <AlertTriangle className="w-4 h-4" /> {error}
          </CardContent>
        </Card>
      )}

      {/* Total summary */}
      <Card data-testid="telemetry-summary">
        <CardContent className="p-4">
          <div className="grid grid-cols-3 gap-4">
            <StatCell label="Total events" value={data?.total_events ?? 0} />
            <StatCell label="Event types" value={events.length} />
            <StatCell label="Window" value={`${days}d`} />
          </div>
        </CardContent>
      </Card>

      {/* Top events chart */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm">Top events</CardTitle>
        </CardHeader>
        <CardContent>
          {top.length === 0 ? (
            <p className="text-xs text-muted-foreground text-center py-8">No events recorded yet.</p>
          ) : (
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={top} layout="vertical" margin={{ left: 80, right: 24 }}>
                <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
                <XAxis type="number" allowDecimals={false} className="text-[10px]" />
                <YAxis dataKey="event" type="category" width={150} className="text-[10px]" />
                <Tooltip
                  contentStyle={{ background: 'rgb(24 24 27)', border: '1px solid rgb(63 63 70)', borderRadius: 6 }}
                  labelStyle={{ color: 'rgb(244 244 245)', fontSize: 12 }}
                  itemStyle={{ color: 'rgb(16 185 129)', fontSize: 12 }}
                />
                <Bar dataKey="count" fill="rgb(16 185 129)" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </CardContent>
      </Card>

      {/* Per-surface cards */}
      <div>
        <p className="text-[10px] font-mono uppercase tracking-[0.22em] text-muted-foreground mb-2">
          Per-surface funnels
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {SURFACES.map((s) => (
            <SurfaceCard
              key={s.key}
              surface={s}
              events={events.filter(e => e.event.startsWith(s.prefix))}
              loading={loading}
            />
          ))}
        </div>
      </div>
    </div>
  );
}
